/**
 * `getAuthState()` — the SYNCHRONOUS read of "is anyone signed in right now."
 *
 * TanStack Router's `beforeLoad` guard runs before a route's component mounts,
 * and it cannot await a network round trip without flashing a blank screen on
 * every navigation. So the guard answers from the access-token cookie alone.
 *
 * SECURITY: these claims are read out of the JWT WITHOUT verifying its
 * signature. That is correct for what they are used for — deciding which screen
 * to draw and whose name to render — and wrong for anything else. Authorization
 * is decided by the service on every single call (security.md); a forged cookie
 * buys someone a rendered shell and a 403 from every request it makes. Never
 * gate a capability on `isAdmin` from here.
 */
import { readAccessToken } from "./sessionCookies";

export type typeSessionUser = {
  userId: string;
  email: string;
  /** Carried for logging and UI hints only — never a permission check. */
  isAdmin: boolean;
  sessionId: string;
};

export type typeAuthState =
  | { status: "signedOut" }
  | {
      status: "signedIn";
      user: typeSessionUser;
      /** Epoch milliseconds, UTC — the `exp` claim, scaled up from seconds. */
      expiresAtMs: number;
    };

export const SIGNED_OUT: typeAuthState = { status: "signedOut" };

type typeRawClaims = {
  userId?: unknown;
  email?: unknown;
  isAdmin?: unknown;
  sessionId?: unknown;
  exp?: unknown;
};

function decodeBase64Url(segment: string): string | null {
  const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
  try {
    return atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
  } catch {
    return null;
  }
}

/**
 * Pulls the claim set out of a JWT's payload segment.
 *
 * Reading `exp` off the token rather than storing an expiry cookie of our own
 * is deliberate: the SDK's reactive refresh writes a new access token without
 * knowing this app exists, and a separate expiry cookie would go stale the
 * moment that happens — leaving the proactive refresh timer either asleep past
 * expiry or spinning in a loop against a timestamp permanently in the past.
 */
export function decodeAccessToken(token: string): {
  user: typeSessionUser;
  expiresAtMs: number;
} | null {
  const parts = token.split(".");
  const payloadSegment = parts[1];
  if (parts.length !== 3 || payloadSegment === undefined) return null;

  const json = decodeBase64Url(payloadSegment);
  if (json === null) return null;

  let claims: typeRawClaims;
  try {
    claims = JSON.parse(json) as typeRawClaims;
  } catch {
    return null;
  }

  const { userId, email, sessionId, exp } = claims;
  if (
    typeof userId !== "string" ||
    typeof email !== "string" ||
    typeof sessionId !== "string" ||
    typeof exp !== "number"
  ) {
    return null;
  }

  return {
    user: { userId, email, sessionId, isAdmin: claims.isAdmin === true },
    expiresAtMs: exp * 1000,
  };
}

/**
 * Reads the current session straight from the cookie. Cheap enough to call on
 * every guard and every render — no request, no promise, no cache to invalidate.
 *
 * An expired token reads as signed OUT. The refresh path is the provider's job
 * (`AuthProvider`), not the guard's: a guard that tried to refresh would have to
 * become async, which is the thing this function exists to avoid.
 */
export function getAuthState(now: number = Date.now()): typeAuthState {
  const token = readAccessToken();
  if (token === undefined || token === "") return SIGNED_OUT;

  const decoded = decodeAccessToken(token);
  if (decoded === null) return SIGNED_OUT;
  if (decoded.expiresAtMs <= now) return SIGNED_OUT;

  return {
    status: "signedIn",
    user: decoded.user,
    expiresAtMs: decoded.expiresAtMs,
  };
}
